/**
 * Analytics event logging service
 * Tracks user actions for product insights and debugging
 */

import { initializeFrpFirebase } from './firebase-client';

interface AnalyticsEvent {
  name: string;
  properties?: Record<string, unknown>;
  timestamp: string;
}

interface AnalyticsInitOptions {
  appName?: string;
  environment?: string;
}

// In-memory queue for offline support
const eventQueue: AnalyticsEvent[] = [];
const MAX_QUEUE_SIZE = 100;
const STORAGE_KEY = 'frp_analytics_events';
const SESSION_KEY = 'frp_analytics_session';

let initialized = false;
let appName = 'FlatRatePro';
let environment = 'unknown';
let userId: string | null = null;
let firebaseSupportChecked = false;
let firebaseSupported = false;

function getSessionId(): string {
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const created = `session_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(SESSION_KEY, created);
    return created;
  } catch {
    return `session_${Date.now()}`;
  }
}

function readStoredEvents(): AnalyticsEvent[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStoredEvents(events: AnalyticsEvent[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
  } catch {
    // Best-effort only.
  }
}

function normalizeEventName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9_]/g, '_').slice(0, 40) || 'event_unknown';
}

function normalizeProperties(properties?: Record<string, unknown>): Record<string, string | number | boolean> {
  if (!properties) return {};

  const normalized: Record<string, string | number | boolean> = {};
  Object.entries(properties).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    const propertyKey = key.replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 40);
    if (!propertyKey) return;

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      normalized[propertyKey] = value;
      return;
    }

    normalized[propertyKey] = JSON.stringify(value).slice(0, 400);
  });

  return normalized;
}

function logToConsole(event: AnalyticsEvent) {
  if (import.meta.env.DEV) {
    console.log(`[Analytics] ${event.name}`, event.properties);
  }
}

function enqueueEvent(event: AnalyticsEvent) {
  eventQueue.push(event);
  if (eventQueue.length > MAX_QUEUE_SIZE) {
    eventQueue.shift(); // Remove oldest if queue is full
  }

  if (typeof window !== 'undefined') {
    const stored = readStoredEvents();
    stored.push(event);
    if (stored.length > MAX_QUEUE_SIZE) {
      stored.splice(0, stored.length - MAX_QUEUE_SIZE);
    }
    writeStoredEvents(stored);
  }
}

async function sendToFirebase(event: AnalyticsEvent) {
  if (typeof window === 'undefined') return;

  try {
    const firebaseAnalytics = await import('firebase/analytics');

    if (!firebaseSupportChecked) {
      firebaseSupportChecked = true;
      firebaseSupported = await firebaseAnalytics.isSupported();
    }
    if (!firebaseSupported) return;

    const client = await initializeFrpFirebase();
    const analytics = firebaseAnalytics.getAnalytics(client.app);

    firebaseAnalytics.logEvent(analytics, normalizeEventName(event.name), normalizeProperties(event.properties));
  } catch {
    // Best-effort only.
  }
}

export function initializeAnalytics(options: AnalyticsInitOptions = {}) {
  if (initialized || typeof window === 'undefined') return;

  appName = options.appName || appName;
  environment = options.environment || environment;

  initialized = true;

  trackEvent('app_launch', {
    app: appName,
    environment,
    path: window.location.pathname,
    referrer: document.referrer || null,
  });
}

export function setAnalyticsUser(nextUserId: string | null) {
  userId = nextUserId;
}

/**
 * Track an analytics event
 */
export function trackEvent(
  name: string,
  properties?: Record<string, unknown>
) {
  const sessionId = typeof window !== 'undefined' ? getSessionId() : 'server';

  const event: AnalyticsEvent = {
    name,
    properties: {
      ...properties,
      app: appName,
      environment,
      session: sessionId,
      user_id: userId,
      path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    },
    timestamp: new Date().toISOString()
  };

  logToConsole(event);
  enqueueEvent(event);
  void sendToFirebase(event);
}

/**
 * Track job-related events
 */
export function trackJobCreated(jobId: string, jobName: string) {
  trackEvent('job_created', { jobId, jobName });
}

export function trackJobEdited(jobId: string, updates: Record<string, unknown>) {
  trackEvent('job_edited', { jobId, updates });
}

export function trackJobDeleted(jobId: string) {
  trackEvent('job_deleted', { jobId });
}

/**
 * Track photo-related events
 */
export function trackPhotoUploaded(jobId: string, photoCount: number, success: boolean) {
  trackEvent('photo_uploaded', { jobId, photoCount, success });
}

export function trackPhotoDeleted(jobId: string) {
  trackEvent('photo_deleted', { jobId });
}

/**
 * Track PDF export
 */
export function trackPDFExported(jobCount: number, filename: string) {
  trackEvent('pdf_exported', { jobCount, filename });
}

/**
 * Track page navigation
 */
export function trackPageView(page: string) {
  trackEvent('page_view', { page });
}

/**
 * Get queued events (for debugging or batch sending)
 */
export function getEventQueue(): AnalyticsEvent[] {
  return [...eventQueue];
}

/**
 * Clear event queue (e.g., after successful send)
 */
export function clearEventQueue() {
  eventQueue.length = 0;
}
